import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

import { SUBHOME_CARROSSEL_TITLE } from './helpers';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_subhome_carrossel_block',
    defaultMessage: 'Carrossel de destaques (sub-home)',
  },
  title: {
    id: 'mpf_subhome_carrossel_title',
    defaultMessage: 'Título da faixa',
  },
  titleHelp: {
    id: 'mpf_subhome_carrossel_title_help',
    defaultMessage: 'Em branco, usa "Carrossel destaques".',
  },
  fieldsetTexto: {
    id: 'mpf_subhome_carrossel_fieldset_texto',
    defaultMessage: 'Card de texto',
  },
  subtitulo: {
    id: 'mpf_subhome_carrossel_subtitulo',
    defaultMessage: 'Chapéu',
  },
  subtituloHelp: {
    id: 'mpf_subhome_carrossel_subtitulo_help',
    defaultMessage: 'Linha acima do texto, ex.: "Conheça o MP Eleitoral".',
  },
  texto: { id: 'mpf_subhome_carrossel_texto', defaultMessage: 'Texto' },
  ctaLabel: {
    id: 'mpf_subhome_carrossel_cta_label',
    defaultMessage: 'Rótulo do botão',
  },
  ctaHref: {
    id: 'mpf_subhome_carrossel_cta_href',
    defaultMessage: 'Destino do botão',
  },
  ctaHrefHelp: {
    id: 'mpf_subhome_carrossel_cta_href_help',
    defaultMessage:
      'Escolha uma página do site ou cole um endereço. O botão só aparece com rótulo e destino preenchidos.',
  },
  ctaExternal: {
    id: 'mpf_subhome_carrossel_cta_external',
    defaultMessage: 'Abrir o botão em nova aba',
  },
  fieldsetCards: {
    id: 'mpf_subhome_carrossel_fieldset_cards',
    defaultMessage: 'Cards',
  },
  items: { id: 'mpf_subhome_carrossel_items', defaultMessage: 'Cards' },
  card: { id: 'mpf_subhome_carrossel_card', defaultMessage: 'Card' },
  cardTitle: {
    id: 'mpf_subhome_carrossel_card_title',
    defaultMessage: 'Legenda',
  },
  cardTitleHelp: {
    id: 'mpf_subhome_carrossel_card_title_help',
    defaultMessage:
      'Até 60 caracteres é o que o Google costuma exibir sem cortar. Acima disso a legenda ainda aparece inteira na página.',
  },
  cardHref: {
    id: 'mpf_subhome_carrossel_card_href',
    defaultMessage: 'Conteúdo',
  },
  cardHrefHelp: {
    id: 'mpf_subhome_carrossel_card_href_help',
    defaultMessage:
      'Página do site ou endereço externo. Ao escolher um conteúdo, a legenda é preenchida com o título dele — legenda já editada não é sobrescrita.',
  },
  cardExternal: {
    id: 'mpf_subhome_carrossel_card_external',
    defaultMessage: 'Abrir em nova aba',
  },
  cardImage: {
    id: 'mpf_subhome_carrossel_card_image',
    defaultMessage: 'Imagem',
  },
  cardImageHelp: {
    id: 'mpf_subhome_carrossel_card_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Em branco, usa a imagem do conteúdo escolhido.',
  },
  cardAlt: {
    id: 'mpf_subhome_carrossel_card_alt',
    defaultMessage: 'Texto alternativo',
  },
  cardAltHelp: {
    id: 'mpf_subhome_carrossel_card_alt_help',
    defaultMessage:
      'Descreva o conteúdo da imagem para leitores de tela. Obrigatório ao enviar imagem própria; herdando a imagem do conteúdo, herda-se também o texto alternativo dele.',
  },
});

/** Campos de um card do carrossel. */
const cardSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.card),
  addMessage: intl.formatMessage(messages.card),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'href', 'external', 'image', 'alt'],
    },
  ],
  properties: {
    // Contador em vez de corte: a legenda aparece inteira na página, então o
    // tamanho é decisão editorial com aviso na hora de escrever. Mesmo
    // tratamento do card de `Blocks/OndeAtua/schema.ts`.
    title: {
      title: intl.formatMessage(messages.cardTitle),
      description: intl.formatMessage(messages.cardTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    href: {
      title: intl.formatMessage(messages.cardHref),
      description: intl.formatMessage(messages.cardHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // `image_field`/`hasPreviewImage`/`image_scales` permitem herdar a
      // imagem do conteúdo sem o editor escolher arte dedicada.
      selectedItemAttrs: [
        '@id',
        '@type',
        'Title',
        'Description',
        'image_field',
        'hasPreviewImage',
        'image_scales',
        // Herda o texto alternativo do conteúdo quando a imagem é herdada.
        'descricaoImagem',
      ],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.cardExternal),
      type: 'boolean',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL.
    image: {
      title: intl.formatMessage(messages.cardImage),
      description: intl.formatMessage(messages.cardImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.cardAlt),
      description: intl.formatMessage(messages.cardAltHelp),
    },
  },
  required: ['title'],
});

/**
 * Schema do bloco "Carrossel de destaques (sub-home)".
 *
 * É a faixa "Onde o MPF atua?" da capa sem as abas: um card de texto e uma
 * única lista de cards. Por isso os campos são achatados (`subtitulo`,
 * `texto`, `items`) em vez dos `tema{N}*` de lá.
 *
 * `activeObject`/`setActiveObject` mantêm o `ObjectListWidget` no modo
 * controlado: `-1` (e não `undefined`) é o que o deixa com tudo fechado — ver
 * `Blocks/Destaques/schema.ts`.
 */
export const mpfSubHomeCarrosselSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    { id: 'default', title: 'Default', fields: ['title'] },
    {
      id: 'texto',
      title: intl.formatMessage(messages.fieldsetTexto),
      fields: ['subtitulo', 'texto', 'ctaLabel', 'ctaHref', 'ctaExternal'],
    },
    {
      id: 'cards',
      title: intl.formatMessage(messages.fieldsetCards),
      fields: ['items'],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: SUBHOME_CARROSSEL_TITLE,
    },
    subtitulo: {
      title: intl.formatMessage(messages.subtitulo),
      description: intl.formatMessage(messages.subtituloHelp),
    },
    texto: {
      title: intl.formatMessage(messages.texto),
      widget: 'textarea',
    },
    ctaLabel: { title: intl.formatMessage(messages.ctaLabel) },
    ctaHref: {
      title: intl.formatMessage(messages.ctaHref),
      description: intl.formatMessage(messages.ctaHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['@id', '@type', 'Title'],
      allowExternals: true,
      initialPath: '/',
    },
    ctaExternal: {
      title: intl.formatMessage(messages.ctaExternal),
      type: 'boolean',
    },
    items: {
      title: intl.formatMessage(messages.items),
      widget: 'object_list',
      schema: cardSchema({ intl }),
      schemaExtender: requireAltWhenOwnImage,
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfSubHomeCarrosselSchema;
